import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Paper,
  Divider
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceLine,
  ReferenceArea,
  BarChart,
  Bar
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Timeline,
  ShowChart,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { format, subDays, isWithinInterval } from 'date-fns';
import { de } from 'date-fns/locale';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const RankingTimeSeries = ({ 
  overview, 
  selectedKeyword, 
  rankingHistory, 
  onKeywordSelect 
}) => {
  const [chartType, setChartType] = useState('line'); // line, area, bar
  const [timeRange, setTimeRange] = useState(90); // days - increased default to capture historical data, 'all' for all time
  const [aggregationLevel, setAggregationLevel] = useState('daily'); // daily, weekly
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  
  // Process ranking history data for time series
  const processTimeSeriesData = () => {
    console.log('📊 TIME SERIES DEBUG: Processing data', {
      rankingHistoryLength: rankingHistory?.length || 0,
      rankingHistoryType: Array.isArray(rankingHistory),
      sampleData: rankingHistory?.[0]
    });
    
    if (!rankingHistory || rankingHistory.length === 0) {
      console.log('❌ TIME SERIES: No ranking history data available');
      return [];
    }

    // Filter data by time range
    let filteredHistory;
    if (timeRange === 'all') {
      console.log('📅 TIME SERIES: Showing all historical data');
      filteredHistory = rankingHistory;
    } else {
      const cutoffDate = subDays(new Date(), timeRange);
      console.log(`📅 TIME SERIES: Filtering data with cutoff date: ${cutoffDate.toISOString()}`);

      filteredHistory = rankingHistory.filter(snapshot => {
        const snapshotDate = new Date(snapshot.snapshotDate || snapshot.analyzedAt);
        const isRecent = snapshotDate >= cutoffDate;

        if (!isRecent) {
          console.log(`⏰ TIME SERIES: Filtering out old snapshot: ${snapshotDate.toISOString()} (before ${cutoffDate.toISOString()})`);
        }

        return isRecent;
      });
    }
    
    console.log(`📊 TIME SERIES: After filtering - ${filteredHistory.length} snapshots remaining (from ${rankingHistory.length} total)`);
    
    if (filteredHistory.length === 0 && rankingHistory.length > 0) {
      console.log('⚠️ TIME SERIES: All snapshots filtered out by time range!');
      console.log('💡 TIME SERIES: Using last 10 snapshots regardless of time range');
      
      // Fallback: Use most recent snapshots if time filtering removes everything
      const fallbackHistory = rankingHistory
        .sort((a, b) => new Date(b.snapshotDate || b.analyzedAt) - new Date(a.snapshotDate || a.analyzedAt))
        .slice(0, 10);
      
      console.log(`🔄 TIME SERIES: Using ${fallbackHistory.length} recent snapshots as fallback`);
      return fallbackHistory;
    }

    // Sort by date
    filteredHistory.sort((a, b) => new Date(a.snapshotDate) - new Date(b.snapshotDate));

    // Process each snapshot into time series data points
    const timeSeriesData = filteredHistory.map(snapshot => {
      console.log('📊 TIME SERIES: Processing snapshot:', {
        snapshotDate: snapshot.snapshotDate,
        hasKeywords: !!(snapshot.keywords),
        keywordsLength: snapshot.keywords?.length || 0,
        overallScore: snapshot.overallScore
      });
      
      const dataPoint = {
        date: format(new Date(snapshot.snapshotDate || snapshot.analyzedAt), 'dd.MM.yy'),
        fullDate: snapshot.snapshotDate || snapshot.analyzedAt,
        overallScore: snapshot.overallScore || 0
      };

      // Add individual keyword rankings from different possible data structures
      if (snapshot.keywords && snapshot.keywords.length > 0) {
        snapshot.keywords.forEach(kw => {
          const keyword = kw.keyword;
          const rank = kw.averageRank || kw.rank;
          
          if (keyword && rank) {
            // Use direct ranking with 1 at the top
            const normalizedRank = Math.min(Math.max(1, rank || 20), 20);
            dataPoint[keyword] = normalizedRank;
            dataPoint[`${keyword}_actual`] = normalizedRank;
            dataPoint[`${keyword}_visibility`] = kw.visibilityScore || Math.max(0, 100 - (rank * 5));
            
            console.log(`📊 TIME SERIES: Added keyword ${keyword} with rank ${rank}`);
          }
        });
      } else if (snapshot.keywordMetrics && snapshot.keywordMetrics.length > 0) {
        // Alternative data structure
        snapshot.keywordMetrics.forEach(kw => {
          const keyword = kw.keyword;
          const rank = kw.averageRank;
          
          if (keyword && rank) {
            // Use direct ranking with 1 at the top
            const normalizedRank = Math.min(Math.max(1, rank || 20), 20);
            dataPoint[keyword] = normalizedRank;
            dataPoint[`${keyword}_actual`] = normalizedRank;
            dataPoint[`${keyword}_visibility`] = kw.visibility || Math.max(0, 100 - (rank * 5));
          }
        });
      }

      return dataPoint;
    });

    return timeSeriesData;
  };

  // Get available keywords from the data
  const getAvailableKeywords = () => {
    if (!overview?.trackedKeywords) return [];
    return overview.trackedKeywords.map(kw => kw.keyword);
  };

  // Get keyword colors for consistent visualization
  const getKeywordColor = (keyword, index) => {
    const colors = [
      amDesignColors.primary, amDesignColors.success, amDesignColors.warning, amDesignColors.error, amDesignColors.primary,
      amDesignColors.primary, amDesignColors.success, amDesignColors.warning, amDesignColors.error, amDesignColors.text.secondary
    ];
    return colors[index % colors.length];
  };

  // Calculate trend for a keyword
  const calculateTrend = (data, keyword) => {
    if (!data || data.length < 2) return null;
    
    const recentData = data.slice(-5);
    const olderData = data.slice(-10, -5);
    
    if (recentData.length === 0 || olderData.length === 0) return null;

    const recentAvg = recentData.reduce((sum, point) => sum + (point[`${keyword}_actual`] || 0), 0) / recentData.length;
    const olderAvg = olderData.reduce((sum, point) => sum + (point[`${keyword}_actual`] || 0), 0) / olderData.length;
    
    if (recentAvg === 0 || olderAvg === 0) return null;
    
    // For rankings, lower is better, so we invert the trend calculation
    const improvement = recentAvg < olderAvg; // Lower rank = better
    const change = Math.abs(recentAvg - olderAvg);
    
    return { improvement, change: change.toFixed(1) };
  };

  // Custom tooltip for the charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ ...amDesignComponents.card, p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }} fontFamily={amDesignTypography.fontFamily.heading}>
            {label}
          </Typography>
          {payload.map((entry, index) => {
            const keyword = entry.dataKey;
            const actualRank = entry.payload[`${keyword}_actual`];

            return (
              <Box key={index} sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontFamily={amDesignTypography.fontFamily.body} style={{ color: entry.color }}>
                  {keyword}: Rang {actualRank  || 'N/A'}

                </Typography>
              </Box>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  const timeSeriesData = processTimeSeriesData();
  const availableKeywords = getAvailableKeywords();
  const keywordsToShow = showAllKeywords ? availableKeywords : (selectedKeyword ? [selectedKeyword] : availableKeywords.slice(0, 3));

  if (!rankingHistory || rankingHistory.length === 0) {
    return (
      <Card sx={{ ...amDesignComponents.card, height: '100%' }}>
        <CardContent sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}>
          <Box>
            <Timeline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom fontFamily={amDesignTypography.fontFamily.heading}>
              Keine Ranking-Verlaufsdaten verfügbar
            </Typography>
            <Typography variant="body2" color="text.secondary" fontFamily={amDesignTypography.fontFamily.body}>
              Ranking-Daten werden aus der Backup-Sammlung geladen...
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={2} display="block" fontFamily={amDesignTypography.fontFamily.body}>
              Debug: rankingHistory.length = {rankingHistory?.length || 0}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        
          

          {/* Time Range */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Zeitraum</InputLabel>
            <Select
              value={timeRange}
              label="Zeitraum"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value={7}>7 Tage</MenuItem>
              <MenuItem value={14}>14 Tage</MenuItem>
              <MenuItem value={30}>30 Tage</MenuItem>
              <MenuItem value={60}>60 Tage</MenuItem>
              <MenuItem value={90}>90 Tage</MenuItem>
              <MenuItem value={'all'}>Alle Zeit</MenuItem>
            </Select>
          </FormControl>

          {/* Show All Keywords Toggle */}
          <ToggleButton
            value={showAllKeywords}
            selected={showAllKeywords}
            onChange={() => setShowAllKeywords(!showAllKeywords)}
            size="small"
          >
            {showAllKeywords ? 'Einzelnes Keyword' : 'Alle Keywords'}
          </ToggleButton>
        </Stack>
      </Box>

      {/* Chart */}
     <Card sx={{ ...amDesignComponents.card, flex: 2, minHeight: 400, overflow: 'visible', p: 2 }}>
        <CardContent sx={{ height: '100%', p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontFamily={amDesignTypography.fontFamily.heading}>
              Ranking-Zeitverlauf
              {selectedKeyword && !showAllKeywords && ` - ${selectedKeyword}`}
            </Typography>
            
            {/* Trend indicators for selected keyword */}
            {selectedKeyword && !showAllKeywords && (() => {
              const trend = calculateTrend(timeSeriesData, selectedKeyword);
              return trend && (
                <Chip
                  icon={trend.improvement ? <TrendingUp /> : <TrendingDown />}
                  label={`${trend.improvement ? 'Verbesserung' : 'Verschlechterung'} um ${trend.change} Plätze`}
                  color={trend.improvement ? 'success' : 'error'}
                  size="small"
                />
              );
            })()}
          </Box>

          {timeSeriesData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={300}>
              {chartType === 'line' && (
                <LineChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={amDesignColors.border} strokeWidth={1.5}/>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    stroke={amDesignColors.text.secondary}
                  />
                  <YAxis
                    domain={[1, 20]}
                    reversed
                    ticks={[2, 4, 6, 8, 10, 12, 14, 16, 18, 20]}
                    tick={{ fontSize: 12 }}
                    label={{
                      value: 'Ranking Position (1 = Top, 20 = Bottom)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fontSize: '12px' }
                    }}
                    stroke={amDesignColors.text.secondary}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  {/* Reference line for excellent ranking */}
                  <ReferenceLine y={18} stroke={amDesignColors.success} strokeDasharray="5 5" />
                  <ReferenceLine y={11} stroke={amDesignColors.warning} strokeDasharray="5 5" />
                  
                  {keywordsToShow.map((keyword, index) => (
                    <Line
                      key={keyword}
                      type="monotone"
                      dataKey={keyword}
                      stroke={getKeywordColor(keyword, index)}
                      strokeWidth={selectedKeyword === keyword ? 3 : 2}
                      dot={{ fill: getKeywordColor(keyword, index), strokeWidth: 2, r: selectedKeyword === keyword ? 6 : 4 }}
                      activeDot={{ r: 8, strokeWidth: 2 }}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              )}

              {chartType === 'area' && (
                <AreaChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={amDesignColors.divider} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke={amDesignColors.text.secondary} />
                  <YAxis
                    domain={[1, 20]} reversed
                    tick={{ fontSize: 12 }}
                    label={{
                      value: 'Ranking Position (1 = Top, 20 = Bottom)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fontSize: '12px' }
                    }}
                    stroke={amDesignColors.text.secondary}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {keywordsToShow.map((keyword, index) => (
                    <Area
                      key={keyword}
                      type="monotone"
                      dataKey={keyword}
                      stroke={getKeywordColor(keyword, index)}
                      fill={`${getKeywordColor(keyword, index)}40`}
                      strokeWidth={2}
                      dot={{ fill: getKeywordColor(keyword, index), strokeWidth: 2, r: 4 }}
                    />
                  ))}
                </AreaChart>
              )}

              {chartType === 'bar' && (
                <BarChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={amDesignColors.divider} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke={amDesignColors.text.secondary} />
                  <YAxis
                    domain={[1, 20]} reversed
                    tick={{ fontSize: 12 }}
                    label={{
                      value: 'Ranking Position (1 = Top, 20 = Bottom)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fontSize: '12px' }
                    }}
                    stroke={amDesignColors.text.secondary}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {keywordsToShow.map((keyword, index) => (
                    <Bar
                      key={keyword}
                      dataKey={keyword}
                      fill={getKeywordColor(keyword, index)}
                      opacity={selectedKeyword === keyword ? 1 : 0.7}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: 300,
              textAlign: 'center'
            }}>
              <Box>
                <ShowChart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Keine Zeitverlaufsdaten verfügbar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Führen Sie mehrere Ranking-Analysen durch, um den Zeitverlauf zu sehen.
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Summary Statistics */ console.log('time sereis',timeSeriesData)}
      {timeSeriesData.length > 0 && selectedKeyword && (

         // <Card sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
         <Card sx={{ ...amDesignComponents.card, mt: 2, height: 'auto', overflow: 'visible' }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontFamily={amDesignTypography.fontFamily.heading}>
              Statistik: {selectedKeyword}
            </Typography>
            <Grid container spacing={5}>
              {(() => {
                const keywordData = timeSeriesData
                  .map(point => point[`${selectedKeyword}_actual`])
                  .filter(Boolean);

                if (keywordData.length === 0) {
                  return (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Keine Daten verfügbar für dieses Keyword
                      </Typography>
                    </Grid>
                  );
                }

                const currentRank = keywordData[keywordData.length - 1];
                const bestRank = Math.min(...keywordData);
                const worstRank = Math.max(...keywordData);
                const avgRank = (keywordData.reduce((sum, rank) => sum + rank, 0) / keywordData.length).toFixed(1);
                
                const firstRank = keywordData[0];
                const lastRank = keywordData[keywordData.length - 1];
                const overallTrend = firstRank && lastRank ? (firstRank - lastRank) : 0;

                return (
                  <>
                    <Grid item xs={6} md={2}>
                      <Box textAlign="center">
                        <Typography variant="h6" color="primary">
                          {currentRank}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Aktuell
                        </Typography>
                        
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box textAlign="center">
                        <Typography variant="h6" color="success.main">
                          {bestRank}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Bester
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box textAlign="center">
                        <Typography variant="h6" color="error.main">
                          {worstRank}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Schlechtester
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box textAlign="center">
                        <Typography variant="h6">
                          {avgRank}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Durchschnitt
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box textAlign="center">
                        <Chip
                          icon={overallTrend > 0 ? <TrendingUp /> : overallTrend < 0 ? <TrendingDown /> : undefined}
                          label={
                            overallTrend > 0 
                              ? `+${overallTrend.toFixed(1)} Plätze besser`
                              : overallTrend < 0 
                              ? `${overallTrend.toFixed(1)} Plätze schlechter`
                              : 'Stabil'
                          }
                          color={overallTrend > 0 ? 'success' : overallTrend < 0 ? 'error' : 'default'}
                          size="small"
                        />
                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                          Gesamttrend
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                );
              })()}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* All Keywords Overview */}
      {showAllKeywords && availableKeywords.length > 0 && (
         //<Card sx={{ mt: 2, maxHeight: 800, overflow: 'auto' }}>
         <Card sx={{ ...amDesignComponents.card, mt: 2, height: 'auto', overflow: 'visible' }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontFamily={amDesignTypography.fontFamily.heading}>
              Alle Keywords - Aktuelle Rankingssssss
            </Typography>
            <Grid container spacing={2}>
              {availableKeywords.map((keyword, index) => {
                const keywordOverview = overview?.trackedKeywords?.find(kw => kw.keyword === keyword);
                const trend = calculateTrend(timeSeriesData, keyword);
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={keyword}>
                    <Paper
                      sx={{
                        ...amDesignComponents.card,
                        p: 2,
                        cursor: 'pointer',
                        border: selectedKeyword === keyword ? `2px solid ${amDesignColors.primary}` : `1px solid ${amDesignColors.border}`,
                        '&:hover': {
                          borderColor: amDesignColors.primary,
                          backgroundColor: amDesignColors.paper
                        }
                      }}
                      onClick={() => onKeywordSelect(keyword)}
                    >
                      <Typography variant="subtitle2" noWrap title={keyword} fontFamily={amDesignTypography.fontFamily.heading}>
                        {keyword}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                        <Typography variant="h6" color="primary" fontFamily={amDesignTypography.fontFamily.heading}>
                          Rang {keywordOverview?.averageRank || 'N/A'}
                        </Typography>
                        {trend && (
                          <Chip
                            icon={trend.improvement ? <TrendingUp /> : <TrendingDown />}
                            label={`${trend.improvement ? '+' : ''}${trend.change}`}
                            color={trend.improvement ? 'success' : 'error'}
                            size="small"
                          />
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RankingTimeSeries;